// Copyright (c) 2020 embyt GmbH. All rights reserved.
// Author: Roman Morawek <roman.morawek@embyt.com>

export interface IMqttConfig {
  mqttEnable: boolean;
  mqttHost: string;
  mqttUser: string;
  mqttPassword: string;
  mqttPrefix: string;
  mqttRetain: boolean;
  cloudEnable?: boolean;
}

// legacy, used in enline <= 7.6
export enum ChargerModelNum {
  OTHER = 0,
  BENDER = 1,
  GOE_HOME = 2,
  PCE_GL = 3,
  KEBA = 4,
  ETREL = 5,
  CIRCTRL = 6,
  OCPP = 7,
  SALIA = 8,
  ALFEN = 9,
  COMPLEO_EBOX = 10,
  COMPLEO_DC = 11,
  KOSTAD = 12,
  HYPERCHARGER = 13,
  ZAPTEC = 14,
  ENERCHARGE = 15,
  COMPLEO_SOLO = 16,
  MENNEKES = 17,
  SIEMENS = 18,
  GREENMOTION = 19,
  GARO_ENTITY = 20,
  SATURN22 = 21,
  EASEE = 22,
}
export enum ChargerModel {
  BENDER = "bender",
  GOE_HOME = "goe",
  PCE_GL = "pce_glb",
  KEBA = "keba",
  ETREL = "etrel",
  CIRCTRL = "circtrl",
  OCPP = "ocpp",
  SALIA = "salia",
  ALFEN = "alfen",
  COMPLEO_EBOX = "compleo_ebox",
  COMPLEO_DC = "compleo_dc",
  KOSTAD = "kostad",
  HYPERCHARGER = "hypercharger",
  ENERCHARGE = "enercharge",
  COMPLEO_SOLO = "compleo_solo",
  MENNEKES = "mennekes",
  SIEMENS = "siemens",
  GREENMOTION = "greenmotion",
  GARO_ENTITY = "garo_entity",
  SATURN22 = "saturn",
  EASEE = "easee",
  KEMPOWER = "kempower",
  SCHRACK_ICHARGE = "schrack_iCharge",
  ZAPTEC = "zaptec",
  QST_ULTRAPOWER = "qst_ultrapower",
}

// default charger channel assignments; might be extended by individual charger models
export enum ChargerEmChannel {
  TOTAL = 1,
}
export enum ChargerInputChannel {
  IS_CONNECTED = 0,
  STATE = 1,
  POWER = 2,
  SESSION_ENERGY = 3,
  TOTAL_ENERGY = 4,
  RFID = 5,
  SOC = 6,
  I1 = 7,
  I2 = 8,
  I3 = 9,
  COMM_ERROR_COUNT = 10,
}

export function parseEnlineChargerName(name: string) {
  // chargepoint identifier may be the whole enline name or the part after an @ (connector-name@charger-name)
  // will be zero on non-match and first char after separator otherwise
  const cpStartChar = name.search(/[@~]/) + 1;
  return {
    chargepoint: name.substring(cpStartChar),
    connector: cpStartChar > 1 ? name.substring(0, cpStartChar - 1) : undefined,
  };
}

export type ICharger = IParameterItem & {
  type: ChargerModelNum | ChargerModel;
  ipAddress: string;
  pollInterval: number; // s
  dpoReqMaxPower: number | null;
  dpoReqMaxCurrent: number | null;
  firstPhase?: number; // enline > 6.2
  backend?: string; // enline >= 6.3.2
  ignoreBackendErrors?: boolean; // enline >= 6.3.2
  regulation?: number | null; // enline >= 7.5
  settings?: Record<string, string | number | boolean>; // enline >= v7.6-463

  // dpiCurPower, dpiAuth, dpiFlags: removed with v7.6-467 and replaced by direct input definitions
  // deprecated but still kept to support LINX/LIOB parameter sets
  dpiCurPower: number | null;
  dpiAuth?: number | null; // enline > 6.2
  dpiFlags?: number | null; // enline > 6.2
  // virtualEM and virtualEMChannel:
  // added as scalar in in enline = 6.2, array in enline > 6.2, removed again in v7.6-464
  // deprecated but still kept to support LINX/LIOB parameter sets
  virtualEM?: number | number[];
  virtualEMChannel?: ChargerEmChannel | ChargerEmChannel[];
  password?: string; // deprecated, now part of settings struct
};

export enum Bus {
  LC = "LC",
  FT = "FT",
  IP = "IP",
  LOCAL = "Local",
  MOD = "Modbus RTU",
  M = "M-Bus",
  VIRTUAL = "Virtual",
  CHARGER = "Charger",
  MODULE = "Module",
}

export function getBusNr(bus: Bus) {
  if (bus === Bus.LC) return 0;
  if (bus === Bus.FT) return 1;
  if (bus === Bus.MOD) return 2;
  if (bus === Bus.M) return 3;
  if (bus === Bus.IP) return 4;
  if (bus === Bus.LOCAL) return 5;
  if (bus === Bus.VIRTUAL) return 6;
  if (bus === Bus.CHARGER) return 7;
  if (bus === Bus.MODULE) return 8;
  // should never happen
  return 8;
}

export function fromBusNr(bus: number) {
  if (bus === 0) return Bus.LC;
  if (bus === 1) return Bus.FT;
  if (bus === 2) return Bus.MOD;
  if (bus === 3) return Bus.M;
  if (bus === 4) return Bus.IP;
  if (bus === 5) return Bus.LOCAL;
  if (bus === 6) return Bus.VIRTUAL;
  if (bus === 7) return Bus.CHARGER;
  if (bus === 8) return Bus.MODULE;
  // should never happen
  return Bus.MODULE;
}

// unfortunately, addresses in switching DB and in parameter set are different!
// we need to translate this
// also see switchinglistmodel.cpp::loadData()
export function dbAddr2ParaAddr(dbItem: IDbAddress, isOutput: boolean): IAddress {
  const bus = fromBusNr(dbItem.bus);
  // for Loytec devices:
  // in parameter set, module numbering starts at 1, except for virtual, module and charger busses
  // in IO DB, module numbering starts at 0, for all busses
  // for embyt devices: no renumbering at all, as we only have virtual/module/charger bus here
  const moduleOffset = (isOutput
    ? [Bus.LC, Bus.FT, Bus.MOD, Bus.M, Bus.IP, Bus.LOCAL]
    : [Bus.MOD, Bus.M]
  ).includes(bus)
    ? 1
    : 0;

  // see address.cpp::getMinOutput()
  // we removed the offset from Bus.Virtual as it seems wrong?
  const channelOffset =
    isOutput && [Bus.LC, Bus.LOCAL].includes(bus) ? 1 : [Bus.FT, Bus.IP].includes(bus) ? 2 : 0;

  return {
    bus,
    module: dbItem.module + moduleOffset,
    channel: dbItem.channel + channelOffset,
  };
}
export function paraAddr2DbAddr(paraItem: IAddress, isOutput: boolean): IDbAddress {
  // see dbAddr2ParaAddr
  const moduleOffset = (isOutput
    ? [Bus.LC, Bus.FT, Bus.MOD, Bus.M, Bus.IP, Bus.LOCAL]
    : [Bus.MOD, Bus.M]
  ).includes(paraItem.bus)
    ? 1
    : 0;
  const channelOffset =
    isOutput && [Bus.LC, Bus.LOCAL].includes(paraItem.bus)
      ? 1
      : [Bus.FT, Bus.IP].includes(paraItem.bus)
      ? 2
      : 0;
  return {
    bus: getBusNr(paraItem.bus),
    module: paraItem.module - moduleOffset,
    channel: paraItem.channel - channelOffset,
  };
}

export interface IDbAddress {
  bus: number;
  module: number;
  channel: number;
}

export interface IAddress {
  bus: Bus;
  module: number;
  channel: number;
}

export interface IParameterItem {
  // the id is used to address the item, i.e. at modules
  // if omitted (legacy), the index of this element within the container-array is used
  id?: number;
  name: string;
  isEnabled: boolean;
  webActive: boolean;
  webCtrl: boolean;
  mqttActive: boolean;
}

export enum ModuleType {
  ALGODUE_UEM80 = "Algodue UEM80",
  CUCULUS_METER = "Cuculus MeterExtension",
  EAP_8DI = "EAP 8DI",
  EAP_8DI4DO = "EAP 8DI.4DO",
  EAP_6DO = "EAP 6DO",
  EATON_EMI3P = "Eaton EMI3P", // Carlo Gavazzi | EM111
  EMBYT = "embyt",
  EMPOWER = "empower", // EEG Oberlungau cloud http post device
  FRONIUS_GEN24 = "Fronius Gen24",
  GAVAZZI_EM111 = "Gavazzi EM111", // Carlo Gavazzi | EM111
  GAVAZZI_EM24 = "Gavazzi EM24",
  HERHOLDT = "Herholdt", // Herholdt | ECS M3Pro
  HUAWEI_SUN2000 = "Huawei Sun2000",
  JANITZA_UMG = "Janitza UMG", // Janitza generic, derived from Janitza MPA-3
  JANITZA_MPA3 = "Janitza MPA-3", // Janitza | MPA-3
  JANITZA_UMG20CM = "Janitza UMG 20CM", // Janitza | UMG 20CM
  JANITZA_UMG801 = "Janitza UMG 801",
  LINX_GATEWAY = "LINX Gateway",
  LIOB_100 = "LIOB-100",
  LIOB_150 = "LIOB-150",
  LIOB_450 = "LIOB-450",
  LIOB_480 = "LIOB-480",
  LIOB_550 = "LIOB-550",
  LIOB_551 = "LIOB-551",
  LIOB_552 = "LIOB-552",
  LIOB_553 = "LIOB-553",
  LIOB_554 = "LIOB-554",
  LIOB_586 = "LIOB-586",
  PLEXLOG = "Plexlog",
  SHELLY = "Shelly",
  SIEMENS = "Siemens PAC2200", // Siemens | PAC2200
  SHRDZM = "Shrdzm",
  TEMPLATE = "Template",
  XSTORAGE = "Eaton xStorage",
}

export function isLiobModule(moduleType: ModuleType) {
  return [
    ModuleType.LIOB_100,
    ModuleType.LIOB_150,
    ModuleType.LIOB_450,
    ModuleType.LIOB_480,
    ModuleType.LIOB_550,
    ModuleType.LIOB_551,
    ModuleType.LIOB_552,
    ModuleType.LIOB_553,
    ModuleType.LIOB_554,
    ModuleType.LIOB_586,
  ].includes(moduleType);
}

export enum ModuleLocalInputChannel {
  TEMPERATURE = 0,
  CPU_LOAD = 1,
}

export enum CuculusEmChannel {
  IMP_TOTAL = 0,
  EXP_TOTAL = 1,
}
export enum ShrdzmEmChannel {
  IMP_TOTAL = 0,
  EXP_TOTAL = 1,
}
export enum EmpowerEmChannel {
  IMP_TOTAL = 0,
  EXP_TOTAL = 1,
}
export enum EmpowerInputChannel {
  I1 = 0,
  I2 = 1,
  I3 = 2,
  U1 = 3,
  U2 = 4,
  U3 = 5,
}

export enum FroniusEmChannel {
  INVERTER = 0,
  MPPT = 1,
  CHARGE = 2,
  DISCHARGE = 3,
}
export enum FroniusInputChannel {
  SOC = 0,
  STATUS = 1,
  DC_POWER = 2,
  CABINET_TEMPERATURE = 3,
}
export enum FroniusOutputChannel {
  REQ_CHARGING_POWER = 0,
  MIN_SOC = 1,
}

export enum HuaweiEmChannel {
  DC = 0,
  GRID_EXP = 1,
  GRID_IMP = 2,
}
export enum HuaweiInputChannel {
  PV1_POWER = 0,
  PV2_POWER = 1,
}
export enum HuaweiOutputChannel {
  ACT_POWER_REL = 0,
  ACT_POWER_ABS = 1,
}

export enum XStorageInputChannel {
  SOC = 0, // %
  BATTERY_MODE = 1,
  OPERATION_MODE = 2,
  CABINET_TEMPERATURE = 3, // C
}
export enum XStorageOutputChannel {
  REQ_CHARGING_POWER = 0, // kW, power for charging (positive) or discharging (negative)
  REQ_CHARGING_SOC = 1, // %, limit SoC for stop of charging/discharging
  REQ_CHARGING_TIME = 2, // h, maximum time for charging/discharging
  MIN_SOC = 3, // %, minimum SoC, kept even when no charging command is active
  POWER_ON = 4, // boolean, power the inverter on/off
}
export enum XStorageEmChannel {
  GRID = 0,
  CHARGE = 1,
}

export enum ShellyInputChannel {
  DIGITAL_0 = 0,
  ANALOG_0 = 1,
  TEMPERATURE_0 = 2,
  TEMPERATURE_BLU_0 = 3,
  HUMIDITY_BLU_0 = 4,
  BATTERY_BLU_0 = 5,
  TEMPERATURE_BLU_1 = 6,
  HUMIDITY_BLU_1 = 7,
  BATTERY_BLU_1 = 8,
}
export enum ShellyOutputChannel {
  SWITCH_0 = 0,
  SWITCH_1 = 1,
  VIRTUAL_0 = 2,
  VIRTUAL_1 = 3,
}

export enum AlgodueEmChannel {
  IMP_TOTAL = 0,
  IMP_L1 = 1,
  IMP_L2 = 2,
  IMP_L3 = 3,
  IMP_REACTIVE_IND = 4,
  IMP_REACTIVE_CAP = 5,
  EXP_TOTAL = 6,
  TOTAL = 7,
}

export enum EatonEmi3pEmChannel {
  IMP_TOTAL = 0,
  EXP_TOTAL = 1,
  TOTAL = 2,
}
export enum EatonEmi3pInputChannel {
  I1 = 0,
  I2 = 1,
  I3 = 2,
  U1 = 3,
  U2 = 4,
  U3 = 5,
  P1 = 6,
  P2 = 7,
  P3 = 8,
}

export enum GavazziEm24EmChannel {
  IMP_TOTAL = 0,
  IMP_L1 = 1,
  IMP_L2 = 2,
  IMP_L3 = 3,
  IMP_REACTIVE = 4,
  EXP_TOTAL = 5,
  TOTAL = 6,
}
export enum GavazziEm111EmChannel {
  IMP_TOTAL = 0,
}
export enum GavazziEm111InputChannel {
  I1 = 0,
  U1 = 3,
}

export enum HerholdtEmChannel {
  IMP_TOTAL = 0,
}
export enum HerholdtInputChannel {
  I1 = 0,
  I2 = 1,
  I3 = 2,
  U1 = 3,
  U2 = 4,
  U3 = 5,
}

export enum JanitzaMpaEmChannel {
  IMP_TOTAL = 0,
  EXP_TOTAL = 1,
  TOTAL = 2,
}
export enum JanitzaMpaInputChannel {
  I1 = 0,
  I2 = 1,
  I3 = 2,
  U1 = 3,
  U2 = 4,
  U3 = 5,
}

export enum JanitzaUmgEmChannel {
  IMP_TOTAL = 0,
  EXP_TOTAL = 1,
  TOTAL = 2,
  REACTIVE = 3,
}
export enum JanitzaUmgInputChannel {
  I1 = 0,
  I2 = 1,
  I3 = 2,
  U1 = 3,
  U2 = 4,
  U3 = 5,
  THDU1 = 6,
  THDU2 = 7,
  THDU3 = 8,
  EXT_TEMPERATURE = 9,
}

export enum PlexlogEmChannel {
  IMP_TOTAL = 0,
  EXP_TOTAL = 1,
  TOTAL = 2,
}
export enum PlexlogInputChannel {
  PV_TOTAL_POWER = 0,
}

export enum SiemensEmChannel {
  IMP_TOTAL = 0,
  EXP_TOTAL = 1,
  TOTAL = 2,
}
export enum SiemensInputChannel {
  I1 = 0,
  I2 = 1,
  I3 = 2,
  U1 = 3,
  U2 = 4,
  U3 = 5,
}

export type IModule = IParameterItem & {
  type: ModuleType;
  address: string;
  pollInterval: number; // s
  comment: string;
  template?: ITemplate | null;
  password?: string; // deprecated, now part of settings struct
  settings?: Record<string, string | number | boolean>; // since v7.6-203
};

export interface ITemplate {
  name: string;
  creator?: string;
  version?: string;
  device?: {
    vendor: string;
    model: string;
  };
  maxBulkRegisters?: number; // maximum number of registers to read with a single request
  timeout?: number; // ms, overwrite default modbus communication timeout
  continueOnError?: boolean; // continue reading other channels if some fails
  channels: (ITemplateChannelInputOutput | ITemplateChannelEnergymeter)[];
}
export interface ITemplateChannel {
  name: string;
}
export interface ITemplateChannelInputOutput extends ITemplateChannel {
  type: "input" | "output";
  value: ITemplateValue;
}
export interface ITemplateChannelEnergymeter extends ITemplateChannel {
  type: "energymeter";
  energy: ITemplateValue;
  power?: ITemplateValue;
}
export interface ITemplateValue {
  /*
  register: type:addr:datatype:endian
  type: RH3 = read holding register (03)
  addr: register
  datatype: I8, U8, I16, U16, I32, U32, F, D
  endian: BE (big endian), LE (littl endian), ME (middle endian)
  */
  register: string; // "RH3:0:I32:BE"
  // allows to overwrite the address of a specific channel to query multiple modules with the same template
  address?: number;
  // allows scaling of the value
  offset?: number;
  factor?: number;
  onlyPositive?: boolean; // negative values are cut to zero
}

export enum ReferenceType {
  INPUT = 0,
  CONDITION = 1,
  ENERGY_METER = 2,
  TARIFF_CIRCUIT_POWER = 3,
  REGULATION_TARGET_VALUE = 4,
  OUTPUT = 5,
  CONSTANT_DERIVED = 6,
  MODULE = 7,
}

export enum ReferenceConstant {
  ZERO = 0,
  DATE = 1, // YYMMDD
  TIME = 2, // s after midnight
  TIMESTAMP = 3, // minutes after 2015
  EPEX_PRICE = 4, // EPEX Spot energy prices from Awattar
  TEMPERATURE = 5, // weather forecast temperature
}
export enum ReferenceCompareType {
  GT = 0,
  GE = 1,
  LT = 2,
  LE = 3,
  EQ = 4,
  NE = 5,
  BITWISE_AND = 6,
}
export enum ConditionBlockPriorityType {
  OFF = 0,
  S1 = 1,
  S10 = 10,
  T1 = 11,
  T10 = 20,
  ON = 21,
  ANALOG_SX = 22,
  ANALOG_TX = 23,
}
export enum ConditionBlockTimeMode {
  CLOCK = 0,
  SUNRISE = 1,
  SUNSET = 2,
}

export enum TargetValueOptimizationFlag {
  NONE = 0,
  LOCKED_1_TV_EXCEEDED = 1,
  LOCKED_2_OPT_TV_EXCEEDED = 2,
  LOCKED_3_OPT_MAX_REACHED = 3,
}

export enum InputType {
  SWITCH = 0,
  PUSHBUTTON_OLD = 1, // not used anymore, only up to enline 4.1
  ANALOG = 2,
  PUSHBUTTON = 3,
  PULSE = 4,
}

// InputUnit was numeric in enline <= 8.0-124 and changed to string type then
export type InputUnit = "date" | "time" | "timestamp" | string | undefined;

export type IInput = IParameterItem &
  IAddress & {
    type: InputType;
    isInverted: boolean;
    unit: InputUnit;
    hysteresis: number; // trending
    interval: number; // trending
    minInterval?: number; // s, trending minimum interval; introduced in enline 7.4
    offset: number; // scaling
    factor: number; // scaling
    fallback?: number; // no/undefined fallback is possible
  };

export enum OutputMode {
  CLOSE = 0,
  OPEN = 1,
  PULSE_ON = 2,
  PULSE_OFF = 3,
  PULSE_BOTH = 4,
  ANALOG = 5,
  PWM = 6,
}

export enum OutputCmd {
  LINX = 0,
  OVERRIDE_ON = 1,
  OVERRIDE_OFF = 2,
  OFF = 3,
  ON = 4,
  TOGGLE = 5,
}

export enum OutputFallbackState {
  ON = 0,
  OFF = 1,
  LAST = 2,
}

export type IOutput = IParameterItem &
  IAddress & {
    mode: OutputMode;
    condition: number | null;
    regulation: number | null;
    power: number;
    fallback: OutputFallbackState;
    pulseTime: number;
    cycleTime: number;
    hysteresis?: number; // trending hysteresis; introduced in enline 7.4
    interval?: number; // s, trending maximum interval; introduced in enline 7.4
    minInterval?: number; // s, trending minimum interval; introduced in enline 7.4
    unit?: string; // introduced in v24.04
  };

export enum ConditionBlockType {
  FALSE = 0,
  CONSTANT = 1,
  CALENDAR = 2,
  DAYTIME = 3,
  WEEKDAY = 4,
  PRIORITY = 5,
  MINOFF = 6,
  MAXOFF = 7,
  MINON = 8,
  PWM = 9,
  VALUE = 10,
  CONDITION = 11,
  COMPARE = 12,
  SEQUENCE = 13,
  ERROR = 14,
  POWER = 15,
  EPEX = 16,
  STATISTIC = 17,
  MAXON = 18,
  WEATHER = 19,
  START = 254,
  END = 255,
}

export interface IConditionBlockBase {
  type: ConditionBlockType;
  invert: boolean;
  active: boolean;
  itsSettings?: string; // binary settings (legacy)
  position: number;
}

export type IConditionBlockReference = IConditionBlockBase & {
  references: {
    type: ReferenceType;
    id: number | null; // reference might be undefined, which is indicated by id null
  }[];
};

export type IConditionBlockAlarm = IConditionBlockReference & {
  alarmSource:
    | "NVAlarm_Targetvalue"
    | "NVAlarm_Restart"
    | "NVAlarm_MissingSync"
    | "NVAlarm_Modul"
    | "NVAlarm_ManualActive"
    | undefined;
};
export type IConditionBlockStatistics = IConditionBlockReference & {
  operation: "min" | "max" | "avg" | "sum" | "count_greater";
  limit: number;
};
export type IConditionBlockWeather = IConditionBlockReference & {
  value: "sunhours" | "temperature";
  observationOffset: number;
};
export type IConditionBlockMaxOff = IConditionBlockBase & {
  maxOffTime: number;
  onTime: number | null;
};
export type IConditionBlockMaxOn = IConditionBlockBase & {
  maxOnTime: number;
  offTime: number | null;
};

export type IConditionBlock =
  | IConditionBlockBase
  | IConditionBlockMaxOff
  | IConditionBlockMaxOn
  | IConditionBlockStatistics;

export type ICondition = IParameterItem & {
  adjacencyMatrix: boolean[][];
  blocks: IConditionBlock[];
  onDelay: number;
  offDelay: number;
  startPos: number;
  endPos: number;
};

export enum EnergyUnit {
  KWH = "kWh",
  NONE = "",
  QM = "m³",
  L = "l",
  KG = "kg",
  KVARH = "kvarh",
}

export type EnergySource = "electrical" | "gas" | "water" | string;

export type IEnergyMeter = IParameterItem &
  IAddress & {
    impulseConstant: number;
    converterFactor: number; // Wandlerfaktor
    number: string;
    comment: string;
    energySource?: EnergySource; // since v7.4-506
    unit: EnergyUnit;
    // obsolete members
    charger?: number | null; // removed in v7.6-464 in favor of IAddress.module
    factorI: number; // removed in v25.04, use converterFactor instead
    factorU?: number; // removed in v25.04, use converterFactor instead
    correction?: number; // %, removed in v25.04, use converterFactor instead
  };

export enum PowerUnit {
  KWH = "kW",
  NONE = "1/h",
  QM = "m³/h",
  L = "l/h",
  KG = "kg/h",
  KVARH = "kvar",
}

// this cost tariff enum is used within the trend-DB only
export enum CostTariff {
  SummerHT = 0,
  SummerLT = 1,
  WinterHT = 2,
  WinterLT = 4,
  Special = 5,
}

export type ITariffCircuit = IParameterItem & {
  regulationPeriod: number;
  unit: PowerUnit;
  syncSource: number | null;
  syncTimeout: number;
  syncSuspension: number;
  extrapolate: number;
  costTariff: number | null; // specific cost tariff; implies CostTariff.Special in trending DB then
  zeroPowerDetection: number; // 0.5 %, "load behavior", thus 2(1%) = dynamic, 20(10%) = normal, 160(80%) = static
  contributions: {
    em: number;
    factor: number;
  }[];
  parent?: number | null; // since v7.4-507
  categories?: string[]; // since v7.4-509
};

export type IRegulation = IParameterItem & {
  tariffline: number | null;
  defaultTargetValue: number;
  targetValueCondition: number | null;
  targetValueOptimization: boolean;
};

export type IGeneral = IMqttConfig & {
  site: string; // site description string; not site name
  longitude?: number; // introduced in enline 7.4
  latitude?: number; // introduced in enline 7.4
  activationPoints: number[];
  deactivationPoints: number[];
  costTariffs: {
    cost: number;
    condition: number | null;
  }[];
  defaultCostTariff: number;
  enabledAlarms: number;
  autoRestart?: boolean; // introduced in enline 8.0.1
  intIntersection: number;
  maxLoopDelay: number;
  maxOptimization: number;
  minTrendingPeriod: number;
  optimizationPoints: {
    energy: number;
    factor: number;
  }[];
  optimizationCondition: number | null;
  optimizationPeriod: number;
  peakCostTariff: number;
  trendCalcCycle: number;
  trendLevelOffDelay: number;
  trendLevelOnDelay: number;
  trendOutputOnDelay: number;
  modbusTcpEnable?: boolean; // introduced in v8.0-91
  modbusRtuEnable?: boolean; // introduced in v8.0-91
  modbusRtuSettings?: string; // introduced in v8.0-91
  modbusEnableInputWrite?: boolean; // introduced in v8.0-91

  customSettings?: ICustomSettings; // since v25.05
};

export interface ICustomSettings {
  customDashboard?: CustomDashboard;
}

export interface CustomDashboardValues {
  tempOut?: number;
  tempIn?: number;
  savings?: number; // e.g. to show CO2 savings
  socBattery?: number;
  socCustom1?: number;
}
export interface CustomDashboard {
  title?: string;
  preTitle?: string;
  sitetext1?: string;
  sitetext2?: string;
  tariffCircuits: {
    gridImport?: number[];
    gridExport?: number[];
    battery?: number[];
    pv?: number[];
    home?: number[];
    custom1?: number[]; // e-mobility
    custom2?: number[];
    custom3?: number[]; // other
  };
  custom3Name?: string; // overwrite name for "other" tile
  timerange?: number; // days, time to use for graph and energy data
  graph?: {
    type?: "line" | "bar";
    aggregation?: "HOUR" | "DAY" | "MONTH" | "NONE";
    hideCustom1?: boolean;
    hideCustom2?: boolean;
    hideCustom3?: boolean;
    separateGridImport?: boolean; // show grid import/export in separate graph
    showAllPositive?: boolean; // show negative values as positive graphs
  };
  // special values can be obtained from inputs or outputs
  inputs?: CustomDashboardValues;
  outputs?: CustomDashboardValues;
  nominalPower: number; // used to normalize the animation speed
}

export enum SequenceCmd {
  OFF = 0,
  ON = 1,
  TOGGLE = 2,
}

export interface ISequenceEntry {
  delay: number;
  output: number;
  state: SequenceCmd;
}

export type ISequence = IParameterItem &
  IAddress & {
    mode: OutputMode;
    condition: number | null;
    entries: ISequenceEntry[];
    startIndex?: number; // only needed on Loytec device, might get removed in future
  };

// definition of parameter set
export interface IParameter {
  comment?: string;

  general: IGeneral;
  input: IInput[];
  condition: ICondition[];
  energymeter: IEnergyMeter[];
  tariffline: ITariffCircuit[];
  regulation: IRegulation[];
  output: IOutput[];
  charger: ICharger[];
  module: IModule[];
  sequence?: ISequence[]; // since 7.4-551
}

// also refer to dbtypes.ts, which describes the source definition as stored in the DB parameter set
export interface IVirtSiteAssign {
  input?: IVirtAssign[];
  energymeter?: IVirtAssign[];
  tariffline?: IVirtAssign[];
  regulation?: IVirtAssign[];
  output?: IVirtAssign[];
  charger?: IVirtAssign[];
  ocpp?: IVirtAssign[];
}

export interface IVirtAssign {
  sourceSite: number;
  sourceIndex: number; // the resolved array index of the source parameter set
  destIndex?: number; // the resolved array index in the destination parameter set; only undefined for ocpp
  // possibilities to overwrite source parameter, e.g. its name
  name?: string;
  isEnabled?: boolean;
  webActive?: boolean;
  webCtrl?: boolean;
  // used to add custom columns to reports
  reportColumn?: ICloudReportColumnDefinition[];
}

export interface ICloudReportColumnDefinition {
  title: string;
  value: string;
}

// extended parameters, as used in the cloud
export type ICloudParameter = IParameter & {
  virtualSite: IVirtSiteAssign;
};

// extended parameters, as used in the LINX/embyt controller
export type ILinxParameter = IParameter & {
  siteName: string;
};
